/**
 * Vista del álbum tipo libro doble página, inspirada en la app oficial
 * FIFA Panini Collection. Muestra un equipo por vista, con flechas para
 * navegar entre equipos en orden del sorteo (grupos A→L), igual que el álbum físico.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Icon } from "../lib/Icon";
import { playerInitials } from "../lib/components";
import { useCatalog } from "../contexts/CatalogContext";
import { useInventory } from "../contexts/InventoryContext";
import { useToast } from "../lib/toast";
import { StickerPhotoModal } from "../lib/StickerPhotoModal";
import { usePhotos } from "../contexts/PhotosContext";
import type { Sticker, Team } from "../api/catalog";
import type { Route } from "../App";

interface Props {
  initialTeamId?: number | null;
  go: (r: Route) => void;
}

interface PageEntry {
  team: Team;
  stickers: Sticker[];
  groupTeams: Team[];   // los 4 del mismo grupo (incluyendo este)
}

export function AlbumBookScreen({ initialTeamId, go }: Props) {
  const { teams, stickers, loading: catLoading } = useCatalog();
  const { ownership, cycleCount, promptCount, setCount, loading: invLoading } = useInventory();
  const { primaries, refresh: refreshPhotos } = usePhotos();
  const toast = useToast();

  // Orden del álbum: grupos A→L luego SPECIAL luego COCACOLA
  const orderedPages = useMemo<PageEntry[]>(() => {
    if (teams.length === 0) return [];
    const groupOrder = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
    const teamsByGroup = new Map<string, Team[]>();
    for (const t of teams) {
      const g = t.group || "_";
      if (!teamsByGroup.has(g)) teamsByGroup.set(g, []);
      teamsByGroup.get(g)!.push(t);
    }
    const ordered: Team[] = [];
    // INTRO al principio (Roll of Honour)
    const intro = teams.find((t) => t.code === "INTRO");
    if (intro) ordered.push(intro);
    for (const g of groupOrder) {
      const list = teamsByGroup.get(g) || [];
      ordered.push(...list);
    }
    // Especiales y Coca-Cola al final
    const special = teams.find((t) => t.code === "SPECIAL");
    const coke = teams.find((t) => t.code === "COCACOLA");
    if (special) ordered.push(special);
    if (coke) ordered.push(coke);

    return ordered.map((team) => {
      const teamStickers = stickers
        .filter((s) => s.team_id === team.id)
        .sort((a, b) => a.position - b.position);
      const groupTeams = team.group
        ? (teamsByGroup.get(team.group) || []).filter((t) => t.id !== team.id || true)
        : [];
      return { team, stickers: teamStickers, groupTeams };
    });
  }, [teams, stickers]);

  const [pageIdx, setPageIdx] = useState(0);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const lastIdx = useRef(pageIdx);
  const initialAppliedRef = useRef(false);
  const [search, setSearch] = useState("");
  const [highlightedStickerId, setHighlightedStickerId] = useState<number | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [photoStickerId, setPhotoStickerId] = useState<number | null>(null);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);

  // Cierra el dropdown al hacer click/tap fuera del search-wrap. Más confiable
  // que onBlur del input — en mobile el blur tiene race conditions con touch.
  useEffect(() => {
    if (!searchOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [searchOpen]);

  // Aplicar initialTeamId UNA SOLA VEZ cuando llegan las páginas. Después la
  // navegación interna no se sobreescribe por re-renders del catálogo.
  useEffect(() => {
    if (!initialAppliedRef.current && initialTeamId && orderedPages.length > 0) {
      const idx = orderedPages.findIndex((p) => p.team.id === initialTeamId);
      if (idx >= 0) {
        setPageIdx(idx);
        initialAppliedRef.current = true;
      }
    } else if (orderedPages.length > 0) {
      // Aunque no haya initialTeamId, marcamos como aplicado para no volver a entrar
      initialAppliedRef.current = true;
    }
  }, [initialTeamId, orderedPages]);

  // Resultados de búsqueda: matchea por nombre del jugador, número, código equipo,
  // nombre completo del equipo. Acepta query con o sin guion ("UZB15" = "UZB-15").
  // Top 50 por relevancia, scrolleable (no truncamos resultados válidos).
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    const qNoDash = q.replace(/-/g, "");
    const matches: { sticker: Sticker; team: Team; score: number }[] = [];
    for (const s of stickers) {
      const team = teams.find((t) => t.id === s.team_id);
      if (!team) continue;
      const numLower = s.num.toLowerCase();
      const numNoDash = numLower.replace(/-/g, "");
      const nameLower = s.name.toLowerCase();
      const codeLower = team.code.toLowerCase();
      const teamNameLower = team.name.toLowerCase();
      const haystack = `${nameLower} ${numLower} ${numNoDash} ${codeLower} ${teamNameLower}`;
      if (!haystack.includes(q) && !haystack.includes(qNoDash)) continue;
      // Score: exact num match > num starts with > name starts with > contains
      let score = 0;
      if (numLower === q || numNoDash === qNoDash) score = 100;
      else if (numLower.startsWith(q) || numNoDash.startsWith(qNoDash)) score = 80;
      else if (nameLower.startsWith(q)) score = 60;
      else if (codeLower === q) score = 50;
      else if (teamNameLower.startsWith(q)) score = 40;
      else score = 10;
      matches.push({ sticker: s, team, score });
    }
    // Sort por score, después por num para tener orden determinista
    matches.sort((a, b) => b.score - a.score || a.sticker.num.localeCompare(b.sticker.num));
    return matches.slice(0, 50);
  }, [search, stickers, teams]);

  const goToSticker = (stickerId: number, teamId: number) => {
    const idx = orderedPages.findIndex((p) => p.team.id === teamId);
    if (idx >= 0) setPageIdx(idx);
    setHighlightedStickerId(stickerId);
    setSearch("");
    setSearchOpen(false);
    // Forzar blur del input (cerrar teclado en mobile)
    (document.activeElement as HTMLElement | null)?.blur?.();
    setTimeout(() => setHighlightedStickerId(null), 3000);
  };

  useEffect(() => {
    if (pageIdx > lastIdx.current) setDirection("forward");
    else if (pageIdx < lastIdx.current) setDirection("back");
    lastIdx.current = pageIdx;
  }, [pageIdx]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageIdx]);

  // Swipe horizontal en móvil para cambiar de página
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;
    const dx = endX - touchStartX.current;
    const dy = endY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Solo procesar si es swipe horizontal claro (no scroll vertical)
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx) * 0.7) return;
    if (dx > 0) {
      // swipe right → previous page
      setPageIdx((i) => Math.max(0, i - 1));
    } else {
      // swipe left → next page
      setPageIdx((i) => Math.min(orderedPages.length - 1, i + 1));
    }
  };

  if (catLoading || invLoading) {
    return <div className="page" style={{ padding: 40, textAlign: "center", color: "var(--fg-muted)" }}>Cargando álbum…</div>;
  }
  if (orderedPages.length === 0) return null;

  const current = orderedPages[pageIdx];
  const prev = () => setPageIdx((i) => Math.max(0, i - 1));
  const next = () => setPageIdx((i) => Math.min(orderedPages.length - 1, i + 1));

  const handleClickSticker = async (sId: number, sName: string) => {
    try {
      const cur = ownership[sId] || 0;
      const newVal = cur >= 5 ? 0 : cur + 1;
      await cycleCount(sId);
      const label = newVal === 0 ? "vacía" : newVal === 1 ? "pegada" : `repetida ×${newVal}`;
      toast(`${sName} → ${label}`);
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const handleAdjust = async (sId: number, delta: number, sName: string) => {
    const cur = ownership[sId] || 0;
    const newVal = Math.max(0, Math.min(99, cur + delta));
    if (newVal === cur) return;
    try {
      await setCount(sId, newVal);
      const label = newVal === 0 ? "vacía" : newVal === 1 ? "pegada" : `×${newVal}`;
      toast(`${sName} → ${label}`);
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  return (
    <div
      className="page album-book"
      ref={containerRef}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        <button className="btn btn-secondary btn-sm" onClick={() => go("album")}>
          <Icon name="arrowL" size={14} /> Vista lista
        </button>

        <div ref={searchWrapRef} className="book-search-wrap" style={{ flex: "1 1 240px", maxWidth: 360, position: "relative" }}>
          <div className="search-box">
            <Icon name="search" size={16} />
            <input
              placeholder='Saltar a: "Portugal", "POR-12", "Messi"…'
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
            />
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(""); setSearchOpen(false); }}
                style={{ background: "transparent", border: 0, cursor: "pointer", color: "var(--fg-muted)", padding: 4 }}
                aria-label="Limpiar"
              >
                <Icon name="x" size={14} />
              </button>
            )}
          </div>
          {searchOpen && searchResults.length > 0 && (
            <div className="book-search-results">
              <div className="book-search-results-count">
                {searchResults.length} resultado{searchResults.length === 1 ? "" : "s"}
              </div>
              {searchResults.map(({ sticker, team }) => {
                const count = ownership[sticker.id] || 0;
                return (
                  <button
                    key={sticker.id}
                    type="button"
                    className="book-search-result"
                    onPointerDown={(e) => { e.preventDefault(); goToSticker(sticker.id, team.id); }}
                  >
                    <span style={{ width: 4, height: 22, background: team.color, borderRadius: 2, flexShrink: 0 }} />
                    <b style={{ minWidth: 60, fontSize: 12 }}>{sticker.num}</b>
                    <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
                      {sticker.name}
                    </span>
                    <span className="tiny muted hide-mobile">{team.name}</span>
                    {count >= 2 && <span className="chip amber" style={{ fontSize: 9, padding: "1px 5px" }}>×{count}</span>}
                    {count === 1 && <span className="chip" style={{ fontSize: 9, padding: "1px 5px", background: "var(--primary-soft)", color: "var(--primary)" }}>✓</span>}
                    {count === 0 && <span className="chip magenta" style={{ fontSize: 9, padding: "1px 5px" }}>falta</span>}
                  </button>
                );
              })}
            </div>
          )}
          {searchOpen && search.length >= 2 && searchResults.length === 0 && (
            <div className="book-search-results">
              <div style={{ padding: 16, textAlign: "center", color: "var(--fg-muted)", fontSize: 13 }}>Sin resultados</div>
            </div>
          )}
        </div>

        <span className="tiny muted hide-mobile">← Swipe para cambiar →</span>
        <span className="tiny muted">{pageIdx + 1} de {orderedPages.length}</span>
      </div>

      <div
        className={`book-spread-anim book-anim-${direction}`}
        key={pageIdx}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <BookSpread
          entry={current}
          ownership={ownership}
          primaries={primaries}
          onCycle={handleClickSticker}
          onLongPress={(sId) => promptCount(sId)}
          onAdjust={handleAdjust}
          onPhotoOpen={(sId) => setPhotoStickerId(sId)}
          highlightedStickerId={highlightedStickerId}
        />
      </div>

      {photoStickerId !== null && (() => {
        const s = stickers.find((x) => x.id === photoStickerId);
        if (!s) return null;
        return (
          <StickerPhotoModal
            stickerId={s.id}
            stickerNum={s.num}
            stickerName={s.name}
            catalogImageUrl={s.image_url}
            onClose={() => setPhotoStickerId(null)}
            onChanged={() => refreshPhotos()}
          />
        );
      })()}

      <GroupProgress entry={current} ownership={ownership} allPages={orderedPages} />

      <div className="book-nav">
        <button className="book-nav-btn" onClick={prev} disabled={pageIdx === 0} aria-label="Página anterior">
          <Icon name="arrowL" size={20} />
        </button>
        <div className="book-nav-label">
          <div className="book-nav-team">{current.team.name}</div>
          {current.team.group && <div className="tiny muted">Grupo {current.team.group}</div>}
        </div>
        <button className="book-nav-btn" onClick={next} disabled={pageIdx >= orderedPages.length - 1} aria-label="Página siguiente">
          <Icon name="arrow" size={20} />
        </button>
      </div>
    </div>
  );
}

function GroupProgress({
  entry,
  ownership,
  allPages,
}: {
  entry: PageEntry;
  ownership: Record<number, number>;
  allPages: PageEntry[];
}) {
  const teamProg = useMemo(() => {
    const owned = entry.stickers.filter((s) => (ownership[s.id] || 0) >= 1).length;
    const total = entry.stickers.length;
    const pct = total ? Math.round((owned / total) * 100) : 0;
    return { owned, total, pct };
  }, [entry, ownership]);

  const groupProg = useMemo(() => {
    if (!entry.team.group) return null;
    const groupPages = allPages.filter((p) => p.team.group === entry.team.group);
    let owned = 0, total = 0;
    for (const p of groupPages) {
      total += p.stickers.length;
      owned += p.stickers.filter((s) => (ownership[s.id] || 0) >= 1).length;
    }
    return {
      teams: groupPages.length,
      owned,
      total,
      pct: total ? Math.round((owned / total) * 100) : 0,
    };
  }, [entry, allPages, ownership]);

  return (
    <div className="book-progress-bar">
      <div className="book-progress-row">
        <div className="book-progress-label">
          <b>{entry.team.name}</b> · {teamProg.owned}/{teamProg.total} láminas
        </div>
        <div className="book-progress-track">
          <div className="book-progress-fill" style={{ width: `${teamProg.pct}%`, background: entry.team.color }} />
        </div>
        <div className="book-progress-pct">{teamProg.pct}%</div>
      </div>
      {groupProg && (
        <div className="book-progress-row">
          <div className="book-progress-label tiny muted">
            Grupo {entry.team.group} ({groupProg.teams} equipos) · {groupProg.owned}/{groupProg.total}
          </div>
          <div className="book-progress-track">
            <div
              className="book-progress-fill"
              style={{ width: `${groupProg.pct}%`, background: "var(--accent-amber)" }}
            />
          </div>
          <div className="book-progress-pct tiny muted">{groupProg.pct}%</div>
        </div>
      )}
    </div>
  );
}

interface BookSpreadProps {
  entry: PageEntry;
  ownership: Record<number, number>;
  primaries: Record<number, string>;
  onCycle: (id: number, name: string) => void;
  onLongPress?: (id: number) => void;
  onAdjust?: (id: number, delta: number, name: string) => void;
  onPhotoOpen?: (stickerId: number) => void;
  highlightedStickerId?: number | null;
}

function BookSpread({ entry, ownership, primaries, onCycle, onLongPress, onAdjust, onPhotoOpen, highlightedStickerId }: BookSpreadProps) {
  const { team, stickers, groupTeams } = entry;
  const isIntro = team.code === "INTRO";
  const isSpecial = team.code === "SPECIAL";
  const isCoke = team.code === "COCACOLA";
  const isCokeOrSpecial = isSpecial || isCoke;

  const leftStickers = isIntro
    ? stickers.slice(0, 4)
    : isCokeOrSpecial
      ? stickers.slice(0, Math.ceil(stickers.length / 2))
      : stickers.slice(0, 8);
  const rightStickers = isIntro
    ? stickers.slice(4)
    : isCokeOrSpecial
      ? stickers.slice(Math.ceil(stickers.length / 2))
      : stickers.slice(8);

  const teamColor = team.color;
  // Patrón decorativo según team_id (rotación entre 4 patrones)
  const patternId = team.id % 4;
  const styleVars = { "--team-color-current": teamColor } as CSSProperties;

  return (
    <div className="book-spread" style={styleVars}>
      <div
        className="book-page book-page-left"
        data-pattern={patternId}
        style={{ background: `linear-gradient(135deg, ${teamColor}26 0%, var(--bg-card) 70%)` }}
      >
        <BookPagePattern teamColor={teamColor} variant={patternId} />
        <div className="book-page-header">
          <div className="book-page-we-are">WE ARE</div>
          <div className="book-page-team-name" style={{ color: teamColor }}>
            {team.name.toUpperCase()}
          </div>
          <div className="book-page-federation">
            <div className="book-page-flag" style={{ background: teamColor }} />
            <div className="tiny muted">
              {team.code === "SPECIAL" ? "FIFA Special Stickers" :
               team.code === "COCACOLA" ? "FIFA × Coca-Cola" :
               `Federación ${team.name}`}
            </div>
          </div>
        </div>
        <div className="book-page-grid">
          {leftStickers.map((s) => (
            <SlotView
              key={s.id}
              sticker={s}
              count={ownership[s.id] || 0}
              teamColor={teamColor}
              onClick={() => onCycle(s.id, s.name)}
              onLongPress={onLongPress ? () => onLongPress(s.id) : undefined}
              onAdjust={onAdjust ? (delta) => onAdjust(s.id, delta, s.name) : undefined}
              onPhotoClick={onPhotoOpen ? () => onPhotoOpen(s.id) : undefined}
              primaryPhotoUrl={primaries[s.id]}
              highlighted={highlightedStickerId === s.id}
            />
          ))}
        </div>
      </div>

      <div className="book-page-spine" />

      <div
        className="book-page book-page-right"
        data-pattern={patternId}
        style={{ background: `linear-gradient(225deg, ${teamColor}26 0%, var(--bg-card) 70%)` }}
      >
        <BookPagePattern teamColor={teamColor} variant={patternId} />
        <div className="book-page-grid">
          {rightStickers.map((s) => (
            <SlotView
              key={s.id}
              sticker={s}
              count={ownership[s.id] || 0}
              teamColor={teamColor}
              onClick={() => onCycle(s.id, s.name)}
              onLongPress={onLongPress ? () => onLongPress(s.id) : undefined}
              onAdjust={onAdjust ? (delta) => onAdjust(s.id, delta, s.name) : undefined}
              onPhotoClick={onPhotoOpen ? () => onPhotoOpen(s.id) : undefined}
              primaryPhotoUrl={primaries[s.id]}
              highlighted={highlightedStickerId === s.id}
            />
          ))}
        </div>
        {!isCokeOrSpecial && !isIntro && team.group && groupTeams.length > 0 && (
          <div className="book-group-pill" style={{ background: teamColor }}>
            <div className="book-group-title">GRUPO {team.group}</div>
            <div className="book-group-flags">
              {groupTeams.map((t) => (
                <div
                  key={t.id}
                  className="book-group-flag"
                  style={{ background: t.color }}
                  title={t.name}
                >
                  <span className="tiny" style={{ color: "white", fontWeight: 700, fontSize: 9 }}>{t.code}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {isIntro && <RollOfHonour />}
      </div>
    </div>
  );
}

const WORLD_CHAMPIONS: { year: number; country: string }[] = [
  { year: 1930, country: "Uruguay" },
  { year: 1934, country: "Italia" },
  { year: 1938, country: "Italia" },
  { year: 1950, country: "Uruguay" },
  { year: 1954, country: "Alemania FR" },
  { year: 1958, country: "Brasil" },
  { year: 1962, country: "Brasil" },
  { year: 1966, country: "Inglaterra" },
  { year: 1970, country: "Brasil" },
  { year: 1974, country: "Alemania FR" },
  { year: 1978, country: "Argentina" },
  { year: 1982, country: "Italia" },
  { year: 1986, country: "Argentina" },
  { year: 1990, country: "Alemania FR" },
  { year: 1994, country: "Brasil" },
  { year: 1998, country: "Francia" },
  { year: 2002, country: "Brasil" },
  { year: 2006, country: "Italia" },
  { year: 2010, country: "España" },
  { year: 2014, country: "Alemania" },
  { year: 2018, country: "Francia" },
  { year: 2022, country: "Argentina" },
];

function RollOfHonour() {
  return (
    <div className="roll-of-honour">
      <h3 className="roll-of-honour-title">🏆 Roll of Honour</h3>
      <div className="roll-of-honour-grid">
        {WORLD_CHAMPIONS.map((c) => (
          <div key={c.year} className="roll-of-honour-row">
            <span className="roll-of-honour-year">{c.year}</span>
            <span className="roll-of-honour-country">{c.country}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Patrones decorativos SVG sutiles que reflejan la "personalidad" del equipo. */
function BookPagePattern({ teamColor, variant }: { teamColor: string; variant: number }) {
  const id = `pat-${variant}-${teamColor.replace("#", "")}`;
  return (
    <svg
      className="book-page-pattern"
      width="100%"
      height="100%"
      viewBox="0 0 200 200"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        {variant === 0 && (
          <pattern id={id} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="2" fill={teamColor} fillOpacity="0.18" />
          </pattern>
        )}
        {variant === 1 && (
          <pattern id={id} x="0" y="0" width="60" height="34" patternUnits="userSpaceOnUse">
            <polygon points="30,2 56,17 30,32 4,17" fill="none" stroke={teamColor} strokeWidth="1" strokeOpacity="0.18" />
          </pattern>
        )}
        {variant === 2 && (
          <pattern id={id} x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(20)">
            <line x1="0" y1="25" x2="50" y2="25" stroke={teamColor} strokeWidth="6" strokeOpacity="0.12" />
          </pattern>
        )}
        {variant === 3 && (
          <pattern id={id} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="14" fill="none" stroke={teamColor} strokeWidth="1.2" strokeOpacity="0.16" />
            <circle cx="20" cy="20" r="6" fill="none" stroke={teamColor} strokeWidth="1" strokeOpacity="0.12" />
          </pattern>
        )}
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

interface SlotProps {
  sticker: Sticker;
  count: number;
  teamColor: string;
  onClick: () => void;
  onLongPress?: () => void;
  onAdjust?: (delta: number) => void;
  onPhotoClick?: () => void;
  primaryPhotoUrl?: string;
  highlighted?: boolean;
}

function SlotView({ sticker, count, teamColor, onClick, onLongPress, onAdjust, onPhotoClick, primaryPhotoUrl, highlighted }: SlotProps) {
  const empty = count === 0;
  const dup = count >= 2;
  const isShield = sticker.is_shield;
  const isLegendary = sticker.is_legendary;

  // Display number como aparece en el álbum oficial: "POR 12" (sin guión).
  // Soporta números sin guion (ej. "00") mostrándolos tal cual.
  const [code, posStr] = sticker.num.split("-");
  const displayNum = posStr ? `${code} ${posStr}` : code;

  const cls = ["book-slot"];
  if (empty) cls.push("empty");
  else cls.push("owned");
  if (dup) cls.push("duplicate");
  if (isLegendary) cls.push("legendary");
  if (isShield) cls.push("shield");
  if (highlighted) cls.push("search-highlight");

  // Nombre corto para slots: si es muy largo, lo truncamos
  const displayName = sticker.role === "Foto grupal"
    ? "Plantel"
    : sticker.role === "Escudo"
    ? sticker.name
    : sticker.name;

  const imageToShow = primaryPhotoUrl || sticker.image_url;
  const showImage = !empty && imageToShow;

  return (
    <button
      className={cls.join(" ")}
      onClick={onClick}
      onContextMenu={onLongPress ? (e) => { e.preventDefault(); onLongPress(); } : undefined}
      style={{ "--team-color": teamColor } as CSSProperties}
    >
      <div className="book-slot-num-top">{displayNum}</div>
      <div className="book-slot-content">
        {showImage ? (
          <img
            src={imageToShow!}
            alt={sticker.name}
            className="book-slot-img"
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        ) : isShield ? (
          <div className="book-slot-shield-text">
            {code.slice(0, 3)}
          </div>
        ) : sticker.role === "Foto grupal" ? (
          <div className="book-slot-icon">👥</div>
        ) : sticker.role === "Estadio" ? (
          <div className="book-slot-icon">🏟️</div>
        ) : sticker.role === "Mascota" ? (
          <div className="book-slot-icon">★</div>
        ) : sticker.role === "Trofeo" ? (
          <div className="book-slot-icon">🏆</div>
        ) : (
          <div className="book-slot-shirt-num" style={{ color: teamColor }}>
            {playerInitials(sticker.name)}
          </div>
        )}
      </div>
      {count >= 1 && onAdjust ? (
        <div className="book-slot-qty" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="book-slot-qty-btn"
            onClick={(e) => { e.stopPropagation(); onAdjust(-1); }}
            aria-label="Restar una"
            title={count === 1 ? "Marcar como vacía" : "Tengo una menos"}
          >−</button>
          <button
            type="button"
            className="book-slot-qty-num"
            onClick={(e) => { e.stopPropagation(); onLongPress?.(); }}
            aria-label="Editar cantidad"
            title="Click para escribir cantidad exacta"
          >
            {count}
          </button>
          <button
            type="button"
            className="book-slot-qty-btn"
            onClick={(e) => { e.stopPropagation(); onAdjust(1); }}
            aria-label="Sumar una"
            title="Tengo una más"
          >+</button>
        </div>
      ) : (
        <div className="book-slot-name">{displayName}</div>
      )}
      {/* Fallback: cuando count >= 2 sin onAdjust sigue el chip estático */}
      {dup && !onAdjust && (
        onLongPress ? (
          <span
            className="book-slot-dup"
            role="button"
            onClick={(e) => { e.stopPropagation(); onLongPress(); }}
            title="Click para escribir cantidad exacta"
            style={{ cursor: "pointer" }}
          >
            ×{count}
          </span>
        ) : (
          <div className="book-slot-dup">×{count}</div>
        )
      )}
      {isLegendary && <div className="book-slot-leg-marker">★</div>}
      {isLegendary && !empty && <div className="book-slot-shine" />}
      {!empty && onPhotoClick && (
        <span
          className="book-slot-photo-btn"
          onClick={(e) => { e.stopPropagation(); onPhotoClick(); }}
          role="button"
          aria-label="Ver fotos"
          title="Ver fotos / subir mi foto"
        >
          📸
        </span>
      )}
    </button>
  );
}
